/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { Document, Page } from 'react-pdf'
import { CHECKBOX_CHECKED_VALUE } from 'constants/pdfFormFields'
import { extractPdfFormFields, getManualPdfFields } from 'helpers/pdfFormFields'
import { PdfFormField } from 'types/document-generation.types'

const PreviewHeader = styled.div`
  padding: 0.5rem 0;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`

const PreviewContainer = styled.div`
  flex: 1;
  background-color: #f3f4f6;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;
`

const PreviewWrapper = styled.div`
  background-color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`

const Tab = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 2px solid ${(props) => (props.active ? '#2c83bd' : 'transparent')};
  color: ${(props) => (props.active ? '#2c83bd' : '#6b7280')};
  font-weight: ${(props) => (props.active ? 600 : 400)};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #2c83bd;
  }
`

const FormContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 0.5rem;
`

const FormSection = styled.div`
  margin-bottom: 2rem;
`

const FormSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`

const FormInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #2c83bd;
    box-shadow: 0 0 0 3px rgba(44, 131, 189, 0.1);
  }
`

interface PdfFormPanelProps {
  pdfUrl: string
  title: string
  /** Field keys filled automatically, hidden from manual entry */
  autoFilledKeys: string[]
  /** Exact field names filled automatically, hidden from manual entry */
  autoFilledFieldNames?: string[]
  showCheckboxes: boolean
  values: Record<string, string>
  onChange: (fieldName: string, value: string) => void
  fieldsTabLabel: string
  fieldsHint: string
}

// Template preview plus manual entry of the template's remaining fields
export const PdfFormPanel = ({
  pdfUrl,
  title,
  autoFilledKeys,
  autoFilledFieldNames,
  showCheckboxes,
  values,
  onChange,
  fieldsTabLabel,
  fieldsHint,
}: PdfFormPanelProps) => {
  const [activeTab, setActiveTab] = useState<'preview' | 'manual'>('preview')
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pdfFormFields, setPdfFormFields] = useState<PdfFormField[]>([])

  const onDocumentLoadSuccess = async (pdf: { numPages: number }) => {
    setNumPages(pdf.numPages)

    try {
      setPdfFormFields(await extractPdfFormFields(pdfUrl))
    } catch (error) {
      console.warn('Could not extract form fields from PDF:', error)
      setPdfFormFields([])
    }
  }

  const manualFields = useMemo(
    () => getManualPdfFields(pdfFormFields, autoFilledKeys, autoFilledFieldNames),
    [pdfFormFields, autoFilledKeys, autoFilledFieldNames],
  )
  const textFields = manualFields.filter((field) => field.type !== 'checkbox')
  const checkboxFields = showCheckboxes ? manualFields.filter((field) => field.type === 'checkbox') : []
  const visibleFieldsCount = textFields.length + checkboxFields.length

  return (
    <>
      <TabContainer>
        <Tab type="button" active={activeTab === 'preview'} onClick={() => setActiveTab('preview')}>
          <i className="fas fa-eye" css={{ marginRight: '0.5rem' }}></i>
          Preview
        </Tab>
        <Tab type="button" active={activeTab === 'manual'} onClick={() => setActiveTab('manual')}>
          <i className="fas fa-edit" css={{ marginRight: '0.5rem' }}></i>
          {fieldsTabLabel}
        </Tab>
      </TabContainer>

      {activeTab === 'preview' ? (
        <>
          <PreviewHeader>
            <div css={{ fontWeight: 600, color: '#374151', fontSize: '16px' }}>
              <i className="fas fa-eye" css={{ marginRight: '0.5rem', color: '#2c83bd' }}></i>
              Preview: {title}
            </div>
          </PreviewHeader>

          <PreviewContainer>
            <PreviewWrapper>
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                externalLinkTarget="_blank"
                error={
                  <div css={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
                    <i className="fas fa-exclamation-triangle" css={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                    <div>Failed to load PDF preview</div>
                    <div css={{ fontSize: '14px', marginTop: '0.5rem' }}>
                      The document template may not be available
                    </div>
                  </div>
                }
                loading={
                  <div css={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    <i className="fas fa-spinner fa-spin" css={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                    <div>Loading preview...</div>
                  </div>
                }>
                {Array.from({ length: numPages ?? 0 }, (v, i) => i + 1).map((page) => (
                  <Page
                    key={page}
                    pageNumber={page}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    width={600}
                  />
                ))}
              </Document>
            </PreviewWrapper>
          </PreviewContainer>
        </>
      ) : (
        <FormContainer>
          {visibleFieldsCount > 0 ? (
            <>
              <div css={{ fontSize: '14px', color: '#6b7280', marginBottom: '1rem' }}>{fieldsHint}</div>
              {textFields.length > 0 && (
                <FormSection>
                  <FormSectionTitle>PDF Form Fields ({textFields.length})</FormSectionTitle>
                  <FormGrid>
                    {textFields.map((field) => (
                      <FormField key={field.name}>
                        <FormLabel>{field.name}</FormLabel>
                        <FormInput
                          type="text"
                          value={values[field.name] || ''}
                          onChange={(e) => onChange(field.name, e.target.value)}
                          placeholder={`Enter value for ${field.name}`}
                        />
                      </FormField>
                    ))}
                  </FormGrid>
                </FormSection>
              )}
              {checkboxFields.length > 0 && (
                <FormSection>
                  <FormSectionTitle>Checkboxes ({checkboxFields.length})</FormSectionTitle>
                  <FormGrid>
                    {checkboxFields.map((field) => (
                      <FormField key={field.name}>
                        <label
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#374151',
                            marginBottom: 0,
                            cursor: 'pointer',
                          }}>
                          <input
                            type="checkbox"
                            checked={values[field.name] === CHECKBOX_CHECKED_VALUE}
                            onChange={(e) => onChange(field.name, e.target.checked ? CHECKBOX_CHECKED_VALUE : '')}
                          />
                          {field.name}
                        </label>
                      </FormField>
                    ))}
                  </FormGrid>
                </FormSection>
              )}
            </>
          ) : (
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                color: '#9ca3af',
                padding: '3rem',
              }}>
              <i className="fas fa-info-circle" css={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}></i>
              <div css={{ fontSize: '16px', fontWeight: 500, marginBottom: '0.5rem' }}>No Fields to Enter</div>
              <div css={{ fontSize: '14px' }}>
                {pdfFormFields.length === 0
                  ? 'No form fields found in this PDF template.'
                  : 'All form fields already have values.'}
              </div>
            </div>
          )}
        </FormContainer>
      )}
    </>
  )
}
